import React, { useEffect, useState } from 'react';
import { cn } from './ui/utils';
import { Link, useLocation } from '@remix-run/react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Routes } from '~/constants/routes';

interface Heading {
  id: string;
  text: string;
  level: number;
  children?: Heading[];
}

interface DocPageProps {
  children: React.ReactNode;
  className?: string;
}

interface HeadingProps {
  children: React.ReactNode;
  className?: string;
}

interface SectionTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface PageTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface ContentProps {
  children: React.ReactNode;
  className?: string;
}

interface FooterProps {
  children: React.ReactNode;
  className?: string;
}

interface PreviousLinkProps {
  href: Routes;
  children: React.ReactNode;
  className?: string;
}

interface NextLinkProps {
  href: Routes;
  children: React.ReactNode;
  className?: string;
}

const DocPage = ({ children, className = '' }: DocPageProps) => {
  return <div className={className}>{children}</div>;
};

const Heading = ({ children, className = '' }: HeadingProps) => {
  return (
    <header
      className={cn(
        'flex flex-col gap-2 pb-8 border-b border-border',
        className,
      )}
    >
      {children}
    </header>
  );
};

const SectionTitle = ({ children, className = '' }: SectionTitleProps) => {
  return (
    <div
      className={cn(
        'text-sm text-primary font-semibold tracking-wide',
        className,
      )}
    >
      {children}
    </div>
  );
};

const PageTitle = ({ children, className = '' }: PageTitleProps) => {
  return (
    <h1 className={cn('text-3xl font-bold text-white', className)}>
      {children}
    </h1>
  );
};

const Content = ({ children, className = '' }: ContentProps) => {
  return (
    <main
      className={cn(
        'mt-8 max-w-4xl',
        // Headings - better spacing and tracking
        '[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mb-8 [&_h1]:mt-12 [&_h1]:leading-tight [&_h1]:tracking-tight',
        '[&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mb-6 [&_h2]:mt-10 [&_h2]:leading-tight [&_h2]:tracking-tight',
        '[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-4 [&_h3]:mt-8 [&_h3]:leading-snug [&_h3]:tracking-tight',
        '[&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-white [&_h4]:mb-3 [&_h4]:mt-6 [&_h4]:leading-snug',
        // Text elements - much better readability
        '[&_p]:text-muted-foreground [&_p]:leading-loose [&_p]:mb-6 [&_p]:tracking-wide [&_p]:text-base',
        '[&_strong]:text-white [&_strong]:font-semibold',
        '[&_em]:text-muted-foreground [&_em]:italic',
        // Lists - more spacing and better readability
        '[&_ul]:mb-6 [&_ul]:pl-8 [&_li]:text-muted-foreground [&_li]:mb-2 [&_li]:leading-loose [&_li]:tracking-wide',
        '[&_ol]:mb-6 [&_ol]:pl-8 [&_ol]:list-decimal [&_ol]:space-y-2',
        '[&_ul]:list-disc [&_ul]:space-y-2',
        // Code - better spacing and readability with dark mode styling
        '[&_code]:text-green-400 [&_code]:bg-slate-900/80 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:border [&_code]:border-slate-700/50',
        '[&_pre]:bg-slate-900/90 [&_pre]:border [&_pre]:border-slate-700/50 [&_pre]:rounded-lg [&_pre]:p-6 [&_pre]:mb-8 [&_pre]:overflow-x-auto [&_pre]:leading-relaxed [&_pre]:shadow-lg',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-sm [&_pre_code]:text-green-300 [&_pre_code]:border-0',
        // Links - better readability
        '[&_a]:text-primary [&_a]:no-underline hover:[&_a]:underline [&_a]:font-medium [&_a]:tracking-wide',
        // Blockquotes - much better spacing
        '[&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:py-2 [&_blockquote]:my-8 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:leading-loose [&_blockquote]:tracking-wide [&_blockquote]:text-lg',
        // Tables - better spacing and readability
        '[&_table]:w-full [&_table]:border-collapse [&_table]:mb-8 [&_table]:border-spacing-0',
        '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-semibold [&_th]:text-white [&_th]:tracking-wide',
        '[&_td]:border [&_td]:border-border [&_td]:px-4 [&_td]:py-3 [&_td]:text-muted-foreground [&_td]:leading-relaxed',
        className,
      )}
    >
      {children}
    </main>
  );
};

const Footer = ({ children, className = '' }: FooterProps) => {
  return (
    <footer
      className={cn(
        'mt-16 pt-8 border-t border-border',
        'flex justify-between items-center',
        className,
      )}
    >
      {children}
    </footer>
  );
};

const PreviousLink = ({
  href,
  children,
  className = '',
}: PreviousLinkProps) => {
  return (
    <Link
      to={href}
      className={cn(
        'group flex flex-col gap-1',
        'transition-colors duration-200',
        className,
      )}
    >
      <span className="text-sm font-bold text-white">Previous</span>
      <div className="flex items-center gap-2">
        <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors" />
        <span className="text-sm font-medium text-muted-foreground group-hover:text-white transition-colors">
          {children}
        </span>
      </div>
    </Link>
  );
};

const NextLink = ({ href, children, className = '' }: NextLinkProps) => {
  return (
    <Link
      to={href}
      className={cn(
        'group flex flex-col gap-1 items-end',
        'transition-colors duration-200',
        className,
      )}
    >
      <span className="text-sm font-bold text-white">Next</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground group-hover:text-white transition-colors">
          {children}
        </span>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors" />
      </div>
    </Link>
  );
};

const DocsPageTableOfContents = ({ className }: { className?: string }) => {
  const location = useLocation();
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    // Reset active ID when location changes
    setActiveId('');

    // Function to extract headings from the document
    const extractHeadings = () => {
      const headingElements = document.querySelectorAll('h2, h3');
      const headingsList: Heading[] = [];
      let currentH2: Heading | null = null;

      headingElements.forEach((heading, index) => {
        const level = parseInt(heading.tagName.charAt(1));
        const text = heading.textContent || '';
        let id = heading.id;

        // Generate an ID if the heading doesn't have one
        if (!id) {
          id = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

          // Ensure uniqueness
          let uniqueId = id;
          let counter = 1;
          while (document.getElementById(uniqueId)) {
            uniqueId = `${id}-${counter}`;
            counter++;
          }

          heading.id = uniqueId;
          id = uniqueId;
        }

        const headingItem: Heading = { id, text, level };

        if (level === 2) {
          // New h2 heading
          headingItem.children = [];
          headingsList.push(headingItem);
          currentH2 = headingItem;
        } else if (level === 3 && currentH2) {
          // h3 heading under current h2
          currentH2.children!.push(headingItem);
        }
      });

      setHeadings(headingsList);
    };

    // Extract headings when component mounts and when content changes
    extractHeadings();

    // Set up a mutation observer to watch for content changes
    const observer = new MutationObserver(() => {
      extractHeadings();
    });

    const contentElement = document.querySelector('main');
    if (contentElement) {
      observer.observe(contentElement, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [location.pathname]);

  useEffect(() => {
    // Set up intersection observer to track which heading is currently visible
    const getAllHeadingIds = (headings: Heading[]): string[] => {
      const ids: string[] = [];
      headings.forEach((heading) => {
        ids.push(heading.id);
        if (heading.children) {
          ids.push(...heading.children.map((child) => child.id));
        }
      });
      return ids;
    };

    const headingElements = getAllHeadingIds(headings)
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    if (headingElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);

        if (visibleEntries.length > 0) {
          // Get the first visible heading (topmost)
          const topEntry = visibleEntries.reduce((prev, current) => {
            return prev.boundingClientRect.top < current.boundingClientRect.top
              ? prev
              : current;
          });

          setActiveId(topEntry.target.id);
        }
      },
      {
        rootMargin: '-100px 0% -80% 0%', // Account for navbar height
        threshold: 0,
      },
    );

    headingElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [headings]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 96; // Account for navbar height
      const elementPosition = element.offsetTop - navbarHeight;

      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      });
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto',
        className,
      )}
    >
      <nav className="space-y-2">
        <p className="text-sm font-bold text-white mb-4">On this page</p>
        <ul className="space-y-1">
          {headings.map((heading) => (
            <li key={heading.id}>
              <button
                onClick={() => handleClick(heading.id)}
                className={cn(
                  'block w-full text-left text-sm font-medium transition-colors duration-200 hover:text-white',
                  'border-l-2 border-transparent pl-3 py-1',
                  activeId === heading.id
                    ? 'text-primary border-primary'
                    : 'text-muted-foreground border-transparent',
                )}
              >
                {heading.text}
              </button>
              {heading.children && heading.children.length > 0 && (
                <ul className="ml-4 mt-1 space-y-1">
                  {heading.children.map((child) => (
                    <li key={child.id}>
                      <button
                        onClick={() => handleClick(child.id)}
                        className={cn(
                          'block w-full text-left text-xs transition-colors duration-200 hover:text-white',
                          'border-l-2 border-transparent pl-3 py-1',
                          activeId === child.id
                            ? 'text-primary border-primary'
                            : 'text-muted-foreground border-transparent',
                        )}
                      >
                        {child.text}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

DocPage.Heading = Heading;
DocPage.SectionTitle = SectionTitle;
DocPage.PageTitle = PageTitle;
DocPage.Content = Content;
DocPage.Footer = Footer;
DocPage.PreviousLink = PreviousLink;
DocPage.NextLink = NextLink;
DocPage.TableOfContents = DocsPageTableOfContents;

export { DocPage };
